import { Borders, Row, Workbook, Worksheet } from "exceljs"
import moment from "moment"
import { DataList } from "../types"
import { calculateSummary } from "./calculateSummary"

type ExportExcel = {
  data: DataList[]
  dateFilter: string
  companyName?: string
}

export const exportExcel = async ({
  data,
  dateFilter
}: ExportExcel) => {
  const workbook = new Workbook()
  const worksheet = workbook.addWorksheet("Sheet 1")
  const columns = [
    "NO",
    "BILL ID#",
    "REF PO ID#",
    "DATE",
    "VENDOR",
    "TOTAL",
    "7 DAYS",
    "15 DAYS",
    "21 DAYS",
    "30 DAYS",
    "60+ DAYS",
  ]
  worksheet.views = [{ showGridLines: false }]

  // Title
  makeTitle(worksheet, dateFilter)

  // Header
  const headers = worksheet.addRow(columns)
  headers.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  headers.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF000000" }
    }
    cell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } }
    }
  })

  makeRows(data, worksheet)

  // create xlsx
  await workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `aged-payable-${dateFilter}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

function makeTitle(worksheet: Worksheet, dateFilter: string) {
  worksheet.mergeCells(1, 1, 1, 10)
  const purchaseCell = worksheet.getCell(1, 1)
  purchaseCell.value = "PURCHASE"
  purchaseCell.font = { bold: true, size: 16 }
  purchaseCell.alignment = {
    vertical: "middle",
    horizontal: "center"
  }

  worksheet.mergeCells(2, 1, 2, 10)
  const registerCell = worksheet.getCell(2, 1)
  registerCell.value = "REGISTER AGED PAYABLE"
  registerCell.font = { bold: true, size: 15 }
  registerCell.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true
  }

  worksheet.mergeCells(3, 1, 3, 11)
  const dateCell = worksheet.getCell(3, 1)
  dateCell.value = `DATE: ${dateFilter}`
  dateCell.font = { bold: true, size: 13 }
  dateCell.alignment = {
    vertical: "middle",
    horizontal: "left",
    wrapText: true
  }

  worksheet.getColumn(2).width = 20
}

function makeRows(
  data: ExportExcel["data"],
  worksheet: Worksheet
) {
  let itemRow: Row

  const cellBorder: Partial<Borders> = {
    top: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } }
  }

  data.forEach((item, index) => {
    itemRow = worksheet.addRow([
      index + 1,
      item.trx_code,
      item.trx_code_po,
      moment(item.transaction_date).format("DD/MM/YYYY"),
      item.vendor_name,
      item.total_amount,
      item?.aged_payable <= 7 ? item?.balance : "-",
      item?.aged_payable >= 8 && item?.aged_payable <= 15 ? item?.balance : "-",
      item?.aged_payable >= 16 && item?.aged_payable <= 21 ? item?.balance : "-",
      item?.aged_payable >= 22 && item?.aged_payable <= 30 ? item?.balance : "-",
      item?.aged_payable >= 31 ? item?.balance : "-",
    ])

    itemRow.eachCell((cell) => {
      cell.border = cellBorder
    })
  })

  const calculate = calculateSummary(data)

  // summary
  const summaryTotal = worksheet.addRow(
    Array(8).fill("").concat(["SUMMARY TOTAL", calculate.balance])
  )

  const summary7Days = worksheet.addRow(
    Array(8).fill("").concat(["SUMMARY 7 DAYS", calculate["7_day"]])
  )

  const summary15Days = worksheet.addRow(
    Array(8).fill("").concat(["SUMMARY 15 DAYS", calculate["15_day"]])
  )

  const summary21Days = worksheet.addRow(
    Array(8).fill("").concat(["SUMMARY 21 DAYS", calculate["21_day"]])
  )

  const summary30Days = worksheet.addRow(
    Array(8).fill("").concat(["SUMMARY 30 DAYS", calculate["30_day"]])
  )

  const summary60Days = worksheet.addRow(
    Array(8).fill("").concat(["SUMMARY 60+ DAYS", calculate["60_day"]])
  )

  summaryTotal.getCell(9).font = { bold: true }
  summaryTotal.getCell(10).font = { bold: true }
  summary7Days.getCell(9).font = { bold: true }
  summary7Days.getCell(10).font = { bold: true }
  summary15Days.getCell(9).font = { bold: true }
  summary15Days.getCell(10).font = { bold: true }
  summary21Days.getCell(9).font = { bold: true }
  summary21Days.getCell(10).font = { bold: true }
  summary30Days.getCell(9).font = { bold: true }
  summary30Days.getCell(10).font = { bold: true }
  summary60Days.getCell(9).font = { bold: true }
  summary60Days.getCell(10).font = { bold: true }
}
